import React, { useEffect, useRef, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { ApiError } from '@utils/apiUtils'
import { isMfaCodeComplete, normalizeMfaInput, type MfaCodeMode } from './mfaCode'

interface MfaVerifyFormProps {
  /** 送出驗證碼。成功由外層完成登入（會導頁）；失敗丟 ApiError，code 是中樞的錯誤代碼 */
  onVerify: (code: string) => Promise<void>
  /** 按「取消」回到登入第一步 */
  onCancel: () => void
  /** 驗證時間已過或帳號狀態變了（MFA_TOKEN_INVALID、SESSION_REVOKED）：回到登入第一步並顯示原因 */
  onRestart: (message: string) => void
  /** 帳號被要求開啟兩步驟驗證但還沒設定（MFA_SETUP_REQUIRED） */
  onSetupRequired: (message: string) => void
}

const DESCRIPTIONS: Record<MfaCodeMode, React.ReactNode> = {
  totp: '打開手機的驗證器 App，輸入「Foreverwebs」目前顯示的 6 位數驗證碼。',
  recovery: (
    <>
      輸入開啟兩步驟驗證時保存的備用碼（例如 <span className="whitespace-nowrap font-mono">ABCDE-23456</span>），每組備用碼只能用一次。
    </>
  ),
}

/**
 * 登入第二步：輸入兩步驟驗證碼。
 * 送出中輸入框用 readOnly 而不是 disabled：disabled 會讓焦點跑掉，打錯之後還要再點一次才能重打。
 */
const MfaVerifyForm: React.FC<MfaVerifyFormProps> = ({ onVerify, onCancel, onRestart, onSetupRequired }) => {
  const [mode, setMode] = useState<MfaCodeMode>('totp')
  const [code, setCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lockedUntil, setLockedUntil] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // 進到這一步、切換驗證碼／備用碼時，焦點放在輸入框
  useEffect(() => {
    inputRef.current?.focus()
  }, [mode])

  // 連錯太多次被鎖：時間到了才讓按鈕恢復
  useEffect(() => {
    if (lockedUntil === null) return
    const remaining = lockedUntil - Date.now()
    if (remaining <= 0) {
      setLockedUntil(null)
      return
    }
    const timer = window.setTimeout(() => setLockedUntil(null), remaining)
    return () => window.clearTimeout(timer)
  }, [lockedUntil])

  const isComplete = isMfaCodeComplete(mode, code)
  const isLocked = lockedUntil !== null

  // 等這次的狀態更新畫完（readOnly 拿掉）再把焦點放回去
  const selectInput = () => {
    window.setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCode(normalizeMfaInput(mode, event.target.value))
    if (error && !isLocked) setError(null)
  }

  const switchMode = () => {
    setMode((current) => (current === 'totp' ? 'recovery' : 'totp'))
    setCode('')
    if (!isLocked) setError(null)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!isComplete || isSubmitting || isLocked) return

    setIsSubmitting(true)
    setError(null)
    try {
      await onVerify(code)
      // 成功：外層完成登入後會導頁，按鈕維持「驗證中」直到離開這一頁
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null
      const message = apiError?.message || '驗證失敗，請稍後再試'
      // SESSION_REVOKED：驗證碼對了，但中樞剛好撤銷了這個帳號的登入（mfa_token 也已經用掉），一樣從頭登入
      if (apiError?.code === 'MFA_TOKEN_INVALID' || apiError?.code === 'SESSION_REVOKED') {
        onRestart(message)
        return
      }
      if (apiError?.code === 'MFA_SETUP_REQUIRED') {
        onSetupRequired(message)
        return
      }
      if (apiError?.status === 429 && apiError.retryAfterMs) {
        setLockedUntil(Date.now() + apiError.retryAfterMs)
      }
      setError(message)
      setIsSubmitting(false)
      // 打錯了：焦點留在輸入框並全選，直接打新的一組就會蓋掉
      selectInput()
    }
  }

  const inputId = 'mfa-code'
  const errorId = 'mfa-code-error'

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <ShieldCheck className="h-6 w-6" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-semibold text-secondary-800">輸入兩步驟驗證碼</h2>
        <p className="mt-2 text-sm text-secondary-600">{DESCRIPTIONS[mode]}</p>
      </div>

      <div>
        <label htmlFor={inputId} className="block text-sm font-medium text-secondary-700 mb-2">
          {mode === 'totp' ? '6 位數驗證碼' : '備用碼'}
        </label>
        {mode === 'totp' ? (
          <input
            key="totp"
            ref={inputRef}
            id={inputId}
            name="mfaCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            placeholder="000000"
            value={code}
            onChange={handleChange}
            readOnly={isSubmitting}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className="input-primary text-center font-mono text-2xl tracking-[0.5em]"
          />
        ) : (
          <input
            key="recovery"
            ref={inputRef}
            id={inputId}
            name="recoveryCode"
            type="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            placeholder="XXXXX-XXXXX"
            value={code}
            onChange={handleChange}
            readOnly={isSubmitting}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className="input-primary text-center font-mono text-lg tracking-widest"
          />
        )}
        {error && (
          <p id={errorId} role="alert" className="mt-2 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>

      <button
        type="submit"
        className="btn-primary w-full"
        disabled={!isComplete || isSubmitting || isLocked}
      >
        {isSubmitting ? '驗證中...' : '驗證並登入'}
      </button>

      <div className="flex flex-col items-center gap-3 text-sm">
        <button
          type="button"
          onClick={switchMode}
          disabled={isSubmitting}
          className="font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
        >
          {mode === 'totp' ? '手機不在身邊？改用備用碼' : '改用驗證器 App 的 6 位數驗證碼'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="text-secondary-500 hover:text-secondary-700 disabled:opacity-50"
        >
          取消，回到登入
        </button>
      </div>
    </form>
  )
}

export default MfaVerifyForm
